// TranslationOptionsModal.tsx
// NOTE: Update frontend/src/__tests__/TranslationOptionsModal.test.tsx and run
// `../scripts/test-frontend.sh TranslationOptionsModal` when modifying this modal.
import React, { useState, useEffect } from 'react';
import type { TourProps } from 'antd';
import { Modal, Button, Tabs, Tooltip, Alert, message } from 'antd';
import { QuestionCircleOutlined, CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { getAuth } from "firebase/auth";
import { isTutorialModeRequested, isTutorialSandboxModeEnabled } from './utils/tutorialMode';
import { resolveFrontendApiBase } from './utils/apiBase';
import AppTour, { renderAppTourPanel } from './tours/AppTour';
import { tourTarget, useGuidedTour } from './tours/useGuidedTour';
import { formatAxiosErrorDetail } from './utils/apiError';

const { TabPane } = Tabs;
const API_BASE = resolveFrontendApiBase();
const METADATA_OVERWRITE_REQUIRED = 'METADATA_OVERWRITE_REQUIRED';

interface TranslationOption {
  id?: string;
  style?: string;
  translatedText: string;
  sourceTrans?: string;
  backTranslation?: string;
  desc?: string;
  description?: string;
  score?: number;
  scoreReason?: string;
  recommended?: boolean;
  recommendationBand?: 'strong_fit' | 'good_alternative' | 'viable_with_caveats' | 'weak_fit' | 'existing_translation';
  recommendationLabel?: string;
  rankLabel?: string;
  confidenceLabel?: string;
  riskLabel?: string;
  modelUsed?: string;
}

interface TranslationOptions {
  titleOptions?: TranslationOption[];
  seriesOptions?: TranslationOption[];
}

interface TranslationOptionsModalProps {
  isVisible: boolean;
  onCancel: () => void;
  translationOptions: TranslationOptions;
  translateMetadata: {
    title: boolean;
    series: boolean;
    blurb: boolean;
  };
  bookId: string;
  bookLanguage: string;
  targetLanguage: string;
  fetchBookDetails: () => void;
  overwriteFlag?: boolean;
  tutorialBypassSave?: boolean;
  tourUserId?: string;
  onTutorialSaveSelections?: (payload: {
    titleOption: TranslationOption | null;
    seriesOption: TranslationOption | null;
    sourceLanguage: string;
    targetLanguage: string;
  }) => void;
}

const auth = getAuth();

const TranslationOptionsModal: React.FC<TranslationOptionsModalProps> = ({
  isVisible,
  onCancel,
  translationOptions,
  translateMetadata,
  bookId,
  bookLanguage,
  targetLanguage,
  fetchBookDetails,
  overwriteFlag = false,
  tutorialBypassSave = false,
  tourUserId,
  onTutorialSaveSelections,
}) => {
  const [activeTabKeyTOM, setActiveTabKeyTOM] = useState<string>('1');
  const [selectedTitleOption, setSelectedTitleOption] = useState<TranslationOption | null>(null);
  const [selectedSeriesOption, setSelectedSeriesOption] = useState<TranslationOption | null>(null);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState('');
  const isTutorialSaveMode = tutorialBypassSave || isTutorialModeRequested() || isTutorialSandboxModeEnabled();
  const titleOptions = translationOptions.titleOptions || [];
  const seriesOptions = translationOptions.seriesOptions || [];
  const requestedFields = [
    ...(translateMetadata.title ? ['Title'] : []),
    ...(translateMetadata.series ? ['Series name'] : []),
  ];
  const selectedFields = [
    ...(translateMetadata.title && selectedTitleOption ? ['Title'] : []),
    ...(translateMetadata.series && selectedSeriesOption ? ['Series name'] : []),
  ];
  const selectionBlockers = [
    ...(translateMetadata.title && !selectedTitleOption ? ['Choose a title translation.'] : []),
    ...(translateMetadata.series && !selectedSeriesOption ? ['Choose a series name translation.'] : []),
    ...(translateMetadata.title && titleOptions.length === 0 ? ['No title options were returned.'] : []),
    ...(translateMetadata.series && seriesOptions.length === 0 ? ['No series name options were returned.'] : []),
  ];
  const readinessLabel = isSaveEnabled ? 'Ready to save' : 'Action needed';
  const readinessTone = isSaveEnabled ? 'success' : 'warning';
  const totalOptions = titleOptions.length + seriesOptions.length;
  const translationOptionsTour = useGuidedTour({
    userId: tourUserId,
    apiBase: API_BASE,
    flag: 'metadataTranslationOptionsTourSeen',
    enabled: isVisible,
    autoStart: true,
    ready: totalOptions > 0,
  });
  const translationOptionsTourSteps = React.useMemo<TourProps['steps']>(() => ([
    {
      title: 'Review the selection summary',
      description: renderAppTourPanel('This panel tracks which translated metadata fields still need a saved choice.'),
      target: tourTarget('translation-options-summary'),
    },
    {
      title: 'Switch between fields',
      description: renderAppTourPanel('Use the title and series tabs to compare generated options for each requested metadata field.'),
      target: tourTarget('translation-options-tabs', 'translation-options-picker'),
    },
    {
      title: 'Compare recommendations',
      description: renderAppTourPanel('Recommended labels, back translations, and notes help you choose the strongest localized wording.'),
      target: tourTarget('translation-options-choice-list', 'translation-options-picker'),
    },
    {
      title: 'Resolve blockers',
      description: renderAppTourPanel('The save button stays disabled until every requested field has a selected option.'),
      target: tourTarget('translation-options-blockers', 'translation-options-summary'),
    },
    {
      title: 'Save selections',
      description: renderAppTourPanel('Save writes the selected localized metadata back to this book.'),
      target: tourTarget('translation-options-save', 'translation-options-picker'),
    },
  ]), []);

  useEffect(() => {
    if (translateMetadata.title && !translateMetadata.series) {
      setActiveTabKeyTOM('1');
    } else if (!translateMetadata.title && translateMetadata.series) {
      setActiveTabKeyTOM('2');
    }
  }, [translateMetadata.title, translateMetadata.series]);

  useEffect(() => {
    setIsSaveEnabled(
      (translateMetadata.title ? selectedTitleOption !== null : true) &&
      (translateMetadata.series ? selectedSeriesOption !== null : true)
    );
  }, [selectedTitleOption, selectedSeriesOption, translateMetadata]);

  const handleCancel = async () => {
    onCancel();
    fetchBookDetails();
  };

  const handleOptionSelect = (option: TranslationOption, type: 'title' | 'series') => {
    if (type === 'title') {
      setSelectedTitleOption(option);
      if (translateMetadata.series && !selectedSeriesOption) {
        setActiveTabKeyTOM('2');
      }
    } else {
      setSelectedSeriesOption(option);
    }
  };

  const tokenStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 22,
    padding: '1px 8px',
    borderRadius: 999,
    border: '1px solid color-mix(in srgb, var(--border-subtle) 78%, transparent)',
    background: 'var(--surface-muted)',
    color: 'var(--text-secondary)',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.2,
  };

  const selectedTokenStyle: React.CSSProperties = {
    ...tokenStyle,
    borderColor: 'var(--accent-primary)',
    background: 'color-mix(in srgb, var(--surface-info) 58%, var(--surface-base) 42%)',
    color: 'var(--text-main)',
  };

  const getRecommendationLabel = (option: TranslationOption) => {
    if (option.recommendationLabel) return option.recommendationLabel;
    const score = typeof option.score === 'number' ? Math.round(option.score) : null;
    if (score === null) return '';
    if (score >= 85) return 'Strong fit';
    if (score >= 75) return 'Good alternative';
    if (score >= 65) return 'Viable with caveats';
    return 'Weak fit';
  };

  const getRecommendationMeta = (option: TranslationOption) => {
    const label = getRecommendationLabel(option);
    const rankLabel = option.rankLabel || (option.recommended ? 'Best match' : '');
    return [label, rankLabel].filter(Boolean).join(' · ');
  };

  const renderOptionDetails = (option: TranslationOption, isSelected: boolean) => {
    const backTranslation = option.backTranslation || option.sourceTrans || '';
    const description = option.description || option.desc || '';
    const recommendationMeta = getRecommendationMeta(option);
    return (
      <div className="translation-options-choice-content">
        <div className="translation-options-choice-title-row">
          <strong className="translation-options-choice-title">{option.translatedText}</strong>
          <span className="translation-options-choice-badges">
            {isSelected && (
              <span style={selectedTokenStyle}>
                <CheckCircleOutlined style={{ marginRight: 4, color: 'var(--accent-primary)' }} />
                Selected
              </span>
            )}
          {option.recommended && (
              <span style={tokenStyle}>AI recommended</span>
          )}
          </span>
        </div>
        {(recommendationMeta || option.style) && (
          <div className="translation-options-choice-meta">
            {recommendationMeta && <span>{recommendationMeta}</span>}
            {recommendationMeta && option.style && <span> · </span>}
            {option.style && <span>{option.style}</span>}
          </div>
        )}
        {backTranslation && (
          <div className="translation-options-choice-line">
            <strong>Meaning in original language:</strong> {backTranslation}
          </div>
        )}
        {description && (
          <div className="translation-options-choice-line translation-options-choice-line--muted">
            <strong>Description:</strong> {description}
          </div>
        )}
        {(option.scoreReason || option.confidenceLabel || option.riskLabel) && (
          <details
            className="translation-options-choice-details"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <summary>Why this option</summary>
            {(option.confidenceLabel || option.riskLabel) && (
              <span>
                {[option.confidenceLabel, option.riskLabel].filter(Boolean).join(' · ')}
              </span>
            )}
            {option.scoreReason && <span>{option.scoreReason}</span>}
          </details>
        )}
      </div>
    );
  };

  const renderOptionButton = (option: TranslationOption, index: number, type: 'title' | 'series') => {
    const isSelected = type === 'title' ? selectedTitleOption === option : selectedSeriesOption === option;
    return (
      <div
        key={index}
        role="radio"
        aria-checked={isSelected}
        tabIndex={0}
        data-testid={`tutorial:translation-options:${type}-option`}
        className={`translation-options-choice-card ${isSelected ? 'is-selected' : ''}`}
        onClick={() => handleOptionSelect(option, type)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOptionSelect(option, type);
          }
        }}
      >
        <div className="translation-options-choice-inner">
          <span className="translation-options-choice-radio" aria-hidden="true">
            {isSelected && <CheckCircleOutlined />}
          </span>
          <div className="translation-options-choice-main">
            {renderOptionDetails(option, isSelected)}
          </div>
        </div>
      </div>
    );
  };

  const saveSelectedOptions = async () => {
    try {
      setSaveErrorMessage('');
      if (isTutorialSaveMode) {
        onTutorialSaveSelections?.({
          titleOption: selectedTitleOption,
          seriesOption: selectedSeriesOption,
          sourceLanguage: bookLanguage,
          targetLanguage,
        });
        onCancel();
        return;
      }

      if (selectedTitleOption) {
        await axios.post(`https://askpoeai-nswdzfky7q-uc.a.run.app/save-title-translation/`, {
          userId: auth.currentUser?.uid,
          bookId: bookId,
          title: selectedTitleOption.translatedText,
          sourceLang: bookLanguage,
          targetLang: targetLanguage,
          overwriteFlag,
        });
      }

      if (selectedSeriesOption) {
        await axios.post(`https://askpoeai-nswdzfky7q-uc.a.run.app/save-series-translation/`, {
          userId: auth.currentUser?.uid,
          bookId: bookId,
          series: selectedSeriesOption.translatedText,
          sourceLang: bookLanguage,
          targetLang: targetLanguage,
          overwriteFlag,
        });
      }

      onCancel();
      fetchBookDetails();
    } catch (error) {
      const detail = (error as any)?.response?.data?.detail;
      const detailMessage = formatAxiosErrorDetail(error, 'Error saving metadata translation');
      const messageText =
        detail && typeof detail === 'object' && detail.errorKind === METADATA_OVERWRITE_REQUIRED
          ? detail.message || detailMessage
          : detailMessage;
      if (!(detail && typeof detail === 'object' && detail.errorKind === METADATA_OVERWRITE_REQUIRED)) {
        console.error("Error saving Title or Series Translation:", error);
      }
      setSaveErrorMessage(messageText);
      message.error(messageText, 10);
    }
  };

  const footerSummary = (
    <div className="translation-modal-footer-summary" aria-live="polite">
      <span>
        <strong>{selectedFields.length}/{requestedFields.length || 0}</strong> selections made
      </span>
      <span className="translation-modal-footer-summary__separator">·</span>
      <span>
        {bookLanguage || 'Source'} to {targetLanguage || 'target language'}
      </span>
      {!isSaveEnabled ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="translation-modal-footer-summary__warning">Action needed</span>
        </>
      ) : null}
    </div>
  );

  return (
    <>
    <Modal
      title={
        <div className="translation-modal-titlebar">
          <span className="translation-modal-titlebar__title">Select Translation Options</span>
          <Button
            type="text"
            size="small"
            icon={<QuestionCircleOutlined />}
            onClick={translationOptionsTour.start}
            data-tour-id="translation-options-tour-trigger"
            className="translation-modal-titlebar__tour app-tour-trigger"
          >
            Take a tour
          </Button>
        </div>
      }
      visible={isVisible}
      onCancel={handleCancel}
      centered
      className="translation-modal-footer-stable translation-options-modal"
      footer={[
        <React.Fragment key="selection-summary">
          {footerSummary}
        </React.Fragment>,
        <Button key="cancel" onClick={handleCancel} data-testid="tutorial:translation-options:cancel-button">Cancel</Button>,
        <Button 
          key="save" 
          type="primary" 
          className="app-primary-cta"
          onClick={saveSelectedOptions} 
          data-testid="tutorial:translation-options:save-button"
          disabled={!isSaveEnabled}
          data-tour-id="translation-options-save"
        >
          Save
        </Button>
      ]}
      width="min(1040px, calc(100vw - 32px))"
      bodyStyle={{
        maxHeight: 'min(76vh, calc(100vh - 220px))',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px 16px 12px',
      }}
      modalRender={(node) => (
        <div data-testid="tutorial:translation-options:dialog">
          {node}
        </div>
      )}
    >
      <div className="translation-modal-body translation-options-modal-body" data-testid="tutorial:translation-options:modal">
        <aside className="translation-options-sidebar" aria-label="Translation selection progress">
          <section
            className="translation-modal-block translation-options-summary-card"
            aria-labelledby="translation-options-context-heading"
            data-tour-id="translation-options-summary"
            data-testid="tutorial:translation-options:summary"
          >
            <div className="translation-modal-block-header">
              <div>
                <h3 id="translation-options-context-heading">Selection summary</h3>
                <p>Pick one saved value for each requested field.</p>
              </div>
            </div>
            <div className="translation-options-language-route">
              <div className="translation-options-language-route-cell">
                <span className="translation-modal-kicker">Source language</span>
                <strong className="translation-options-language-chip">{bookLanguage || 'Not selected'}</strong>
              </div>
              <div className="translation-options-language-route-divider" aria-hidden="true">
                to
              </div>
              <div className="translation-options-language-route-cell">
                <span className="translation-modal-kicker">Target language</span>
                <strong className="translation-options-language-chip">{targetLanguage || 'Not selected'}</strong>
              </div>
            </div>
            <div className="translation-options-progress">
              <div className="translation-options-progress-label">
                <strong>{selectedFields.length}/{requestedFields.length || 0}</strong>
                <span>selected</span>
              </div>
              <div className="translation-options-progress-track" aria-hidden="true">
                <span style={{ width: `${requestedFields.length ? (selectedFields.length / requestedFields.length) * 100 : 0}%` }} />
              </div>
            </div>
            <div className={`translation-options-status-row translation-options-status-row--${readinessTone}`}>
              <span>{readinessLabel}</span>
              <strong>{isSaveEnabled ? 'All requested fields have selections.' : `${selectionBlockers.length} item${selectionBlockers.length === 1 ? '' : 's'} to resolve.`}</strong>
            </div>
            <div className="translation-options-checklist" aria-live="polite">
              {translateMetadata.title && (
                <button
                  type="button"
                  className={`translation-options-check-row ${selectedTitleOption ? 'is-complete' : 'is-needed'}`}
                  onClick={() => setActiveTabKeyTOM('1')}
                >
                  <span>{selectedTitleOption ? 'Title selected' : 'Choose title'}</span>
                  <strong>{selectedTitleOption?.translatedText || `${titleOptions.length} options`}</strong>
                </button>
              )}
              {translateMetadata.series && (
                <button
                  type="button"
                  className={`translation-options-check-row ${selectedSeriesOption ? 'is-complete' : 'is-needed'}`}
                  onClick={() => setActiveTabKeyTOM('2')}
                >
                  <span>{selectedSeriesOption ? 'Series selected' : 'Choose series'}</span>
                  <strong>{selectedSeriesOption?.translatedText || `${seriesOptions.length} options`}</strong>
                </button>
              )}
            </div>
            {overwriteFlag && (
              <div className="translation-options-requested-fields">
                <label className="translation-options-overwrite-toggle">
                  <input type="checkbox" checked readOnly />
                  <span>Overwrite existing</span>
                </label>
              </div>
            )}
          </section>

          {selectionBlockers.length > 0 && (
            <section className="translation-modal-block translation-options-blockers-card" aria-labelledby="translation-options-blockers-heading" data-tour-id="translation-options-blockers">
              <div className="translation-modal-block-header">
                <div>
                  <h3 id="translation-options-blockers-heading">Next step</h3>
                  <p>{selectionBlockers[0] || 'Review your selections before saving.'}</p>
                </div>
              </div>
              <div className="translation-options-blocker-list">
                {selectionBlockers.map((blocker) => (
                  <Alert key={blocker} message={blocker} type="warning" icon={<InfoCircleOutlined />} showIcon />
                ))}
              </div>
            </section>
          )}
          {saveErrorMessage && (
            <Alert type="error" showIcon message={saveErrorMessage} />
          )}
        </aside>

        <section className="translation-modal-block translation-options-picker" aria-labelledby="translation-options-list-heading" data-tour-id="translation-options-picker">
          <div className="translation-modal-block-header">
            <div>
              <h3 id="translation-options-list-heading">Translation choices</h3>
              <p>Choose one option per requested field. Recommendations are suggestions, not automatic decisions.</p>
            </div>
          </div>
          <Tabs
            data-tour-id="translation-options-tabs"
            defaultActiveKey="1"
            activeKey={activeTabKeyTOM}
            onChange={setActiveTabKeyTOM}
            style={{ marginBottom: 0 }}
            type="card"
          >
            {translateMetadata.title && (
              <TabPane tab={<span data-testid="tutorial:translation-options:title-tab">Title ({titleOptions.length})</span>} key="1">
                <div className="translation-readiness-panel" style={{ marginBottom: 10 }}>
                  <div className="translation-modal-subheading">
                    Title choices
                    <Tooltip title="Choose the option that best captures the meaning and style of your original title.">
                      <QuestionCircleOutlined style={{ color: 'var(--accent-primary)', marginLeft: 8 }} />
                    </Tooltip>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    {selectedTitleOption ? `Selected: ${selectedTitleOption.translatedText}` : `${titleOptions.length} title choice${titleOptions.length === 1 ? '' : 's'} available.`}
                  </div>
                </div>

                {titleOptions.length > 0 ? (
                  <div
                    className="translation-options-choice-list"
                    role="radiogroup"
                    aria-label="Title translation choices"
                    data-tour-id="translation-options-choice-list"
                    data-testid="tutorial:translation-options:title-choices"
                  >
                    {titleOptions.map((option, index) => renderOptionButton(option, index, 'title'))}
                  </div>
                ) : (
                  <Alert message="No title options were returned." type="warning" showIcon />
                )}
              </TabPane>
            )}

            {translateMetadata.series && (
              <TabPane tab={<span data-testid="tutorial:translation-options:series-tab">Series Name ({seriesOptions.length})</span>} key="2">
                <div className="translation-readiness-panel" style={{ marginBottom: 10 }}>
                  <div className="translation-modal-subheading">
                    Series name choices
                    <Tooltip title="Choose the option that best captures the meaning and style of your original series name.">
                      <QuestionCircleOutlined style={{ color: 'var(--accent-primary)', marginLeft: 8 }} />
                    </Tooltip>
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
                    {selectedSeriesOption ? `Selected: ${selectedSeriesOption.translatedText}` : `${seriesOptions.length} series choice${seriesOptions.length === 1 ? '' : 's'} available.`}
                  </div>
                </div>

                {seriesOptions.length > 0 ? (
                  <div
                    className="translation-options-choice-list"
                    role="radiogroup"
                    aria-label="Series translation choices"
                    data-tour-id="translation-options-choice-list"
                    data-testid="tutorial:translation-options:series-choices"
                  >
                    {seriesOptions.map((option, index) => renderOptionButton(option, index, 'series'))}
                  </div>
                ) : (
                  <Alert message="No series name options were returned." type="warning" showIcon />
                )}
              </TabPane>
            )}
          </Tabs>
        </section>
      </div>
    </Modal>
    <AppTour
      open={translationOptionsTour.open}
      steps={translationOptionsTourSteps}
      current={translationOptionsTour.current}
      onChange={translationOptionsTour.setCurrent}
      onClose={translationOptionsTour.close}
      onFinish={translationOptionsTour.close}
      zIndex={2400}
    />
    </>
  );
};

export default TranslationOptionsModal;
